import { Injectable } from '@angular/core';
import { BehaviorSubject, concat, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CreateUserInput } from '../models/CreateUserInput.model';
import { Customer, CreateCustomerInput } from '../models/Customer.model';
import { UserDetail } from '../models/UserDetail.model';
import { PermissionOverride } from '../models/PermissionOverride.model';
import { RoleClaim, AppClaim } from '../models/RoleClaim.model';
import { UpdatePasswordInput } from '../models/UpdatePasswordInput.model';
import { UpdateUserInput } from '../models/UpdateUserInput.model';
import { environment } from '../environments/environment';
import { StorageService } from './storage.service';

export type IAuthenticationResult =
  SuccessAuthenticationResult |
  FailureAuthenticationResult |
  RedirectAuthenticationResult;

export interface SuccessAuthenticationResult {
  status: AuthenticationResultStatus.Success;
  state: any;
}

export interface FailureAuthenticationResult {
  status: AuthenticationResultStatus.Fail;
  message: string;
}

export interface RedirectAuthenticationResult {
  status: AuthenticationResultStatus.Redirect;
}

export enum AuthenticationResultStatus {
  Success,
  Redirect,
  Fail
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizeService {
  private userSubject: BehaviorSubject<UserDetail | null> = new BehaviorSubject<UserDetail | null>(null);
  private baseUrl: string = environment.apiUrl + '/';

  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  public isAuthenticated(): Observable<boolean> {
    return this.getUser().pipe(map(u => !!u));
  }

  public getUser(): Observable<UserDetail | null> {
    return concat(
      this.userSubject.pipe(take(1), filter(u => !!u)),
      this.userSubject.asObservable());
  }

  public getAccessToken(): string | null {
    return this.storageService.getItem("accessToken")
  }

  public getRefreshToken(): string | null {
    return this.storageService.getItem("refreshToken")
  }

  public getRoles(): Observable<any[]> {
    return this.http.get<any>(this.baseUrl + 'roles/search');
  }

  public createRole(roleName: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'roles/create', { roleName: roleName });
  }

  public updateRolePermissions(roleName: string, permissions: RoleClaim[]): Observable<any> {
    let formattedPermissions = permissions.map((claim) => { return { permissionType: claim.type, permissionValue: claim.value }});
    return this.http.post<any>(this.baseUrl + 'roles/update', { roleName: roleName, permissions: formattedPermissions });
  }

  public getAggregateClaims(): Observable<any[]> {
    return this.http.get<any>(this.baseUrl + 'roles/getAggregateClaims');
  }

  public updateClaimTypes(claimsToUpdate: { permissionType: string, permissionValue: string }[] ): Observable<any> {
    const bodyArr = claimsToUpdate.map((claimToUpdate: {permissionType: string, permissionValue: string}) => {
      return { permissionType: claimToUpdate.permissionType, permissionValue: claimToUpdate.permissionValue};
    });
    return this.http.post<any>(this.baseUrl + 'roles/updateClaimTypes', { claimInputs: bodyArr });
  }

  public addRoleUser(roleName: string, email: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'roles/addRoleUser', { roleName: roleName, email: email });
  }

  public removeRoleUser(roleName: string, email: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'roles/removeRoleUser', { roleName: roleName, email: email });
  }

  public deleteRole(roleName: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'roles/delete', { roleName: roleName });
  }

  public getUsersInRole(roleName: string): Observable<any[]> {
    return this.http.post<any>(this.baseUrl + 'roles/getUsers', {
      roleName: roleName
    });
  }

  public getUserRoles(userId: string): Observable<string[]> {
    return this.http.get<string[]>(this.baseUrl + 'users/getRoles/' + userId);
  }

  public getUserPermissions(userId: string): Observable<AppClaim[]> {
    return this.http.get<AppClaim[]>(this.baseUrl + 'users/getUserPermissions/' + userId);
  }

  public getPermissions(): Observable<string[]> {
    return this.http.get<any>(this.baseUrl + 'users/getPermissions');
  }

  public removePermissionOverride(permissionOverride: PermissionOverride): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'users/removePermissionOverride', { 
      userId: permissionOverride.UserId, 
      overrideType: permissionOverride.OverrideType, 
      permission: permissionOverride.Permission 
    });
  }

  public addPermissionOverride(permissionOverride: PermissionOverride): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'users/addPermissionOverride', { 
      userId: permissionOverride.UserId, 
      overrideType: permissionOverride.OverrideType, 
      permission: permissionOverride.Permission 
    });
  }

  public getUserDetails(userId: string): Observable<UserDetail> {
    return this.http.get<UserDetail>(this.baseUrl + 'users/getDetails/' + userId);
  }

  public getUsers(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'users/search');
  }

  public searchUsers(pageNum: number, pageSize: number, query?: string): Observable<any[]> {
    return this.http.post<any>(this.baseUrl + 'users/search', {
      pageNum: pageNum,
      pageSize: pageSize,
      query: query ? query : null
    });
  }

  public createUser(createUserInput: CreateUserInput): Observable<boolean> {
    return this.http.post<any>(this.baseUrl + 'users/create', {
      email: createUserInput.Email,
      userName: createUserInput.UserName,
      password: createUserInput.Password,
      firstName: createUserInput.FirstName,
      lastName: createUserInput.LastName
    });
  }

  public getCustomers(): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.baseUrl + 'users/getCustomers');
  }

  public getMechanics(): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.baseUrl + 'users/getMechanics');
  }

  public createCustomer(createCustomerInput: CreateCustomerInput): Observable<{ userId: string }> {
    // Empty strings are omitted so the API's @IsOptional validators skip them.
    return this.http.post<{ userId: string }>(this.baseUrl + 'users/createCustomer', {
      email: createCustomerInput.Email || undefined,
      phoneNumber: createCustomerInput.PhoneNumber || undefined,
      userName: createCustomerInput.UserName || undefined,
      firstName: createCustomerInput.FirstName,
      lastName: createCustomerInput.LastName
    });
  }

  public updateUser(updateUserInput: UpdateUserInput): Observable<boolean> {
    return this.http.post<any>(this.baseUrl + 'users/update', {
      userId: updateUserInput.UserId,
      newEmail: updateUserInput.NewEmail,
      userName: updateUserInput.UserName,
      isActive: updateUserInput.IsActive,
      firstName: updateUserInput.FirstName,
      lastName: updateUserInput.LastName,
      phoneNumber: updateUserInput.PhoneNumber,
      hourlyRate: updateUserInput.HourlyRate,
      emailNotificationsEnabled: updateUserInput.EmailNotificationsEnabled
    });
  }

  public updatePassword(updatePasswordInput: UpdatePasswordInput): Observable<boolean> {
    return this.http.post<any>(this.baseUrl + 'users/updatePassword', {
      email: updatePasswordInput.email,
      password: updatePasswordInput.password
    });
  }

  public forgotPassword(email: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      this.baseUrl + 'auth/forgot-password', { email });
  }

  public resetPassword(email: string, token: string, password: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      this.baseUrl + 'auth/reset-password', { email, token, password });
  }

  public login(email: string, password: string, rememberMe: boolean): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'auth/login', {
      email: email,
      password: password,
      rememberMe: rememberMe
    });
  }

  public setUserSubject(user: UserDetail | null) {
    this.userSubject.next(user);
  }

  // The refresh token (a JWT signed with the same secret) authenticates this
  // call explicitly, so the expired access token in storage is never used and
  // never has to be stomped over.
  public getNewAccessTokenSilently(refreshToken: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'users/getAccessTokenSilently', {
      headers: { Authorization: `Bearer ${refreshToken}` }
    });
  }

  public refreshToken(refreshToken: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'auth/refresh-token', {
      refreshToken: refreshToken
    });
  }

  public logout(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.setUserSubject(null);
  }

  public confirmRegistration(email: string, token: string): Observable<{ success: boolean; message: string }> {
    return this.http.get<{ success: boolean; message: string }>(
      this.baseUrl + 'users/confirm-registration',
      { params: { email, token } }
    );
  }
}